import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccessTime,
  Assignment,
  CheckCircle,
  Warning,
  BugReport,
  Speed,
  Timeline,
  EventAvailable,
  Refresh,
  Download,
  Info
} from '@mui/icons-material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const UserProductivity: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [productivityData, setProductivityData] = useState<any>(null);
  const [teamComparison, setTeamComparison] = useState<any[]>([]);
  const [weeklyHours, setWeeklyHours] = useState(40);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date()
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadProductivityData();
    }
  }, [selectedUserId, dateRange, weeklyHours]);

  const loadUsers = async () => {
    try {
      const usersData = await apiService.getAllUsers();
      setUsers(usersData);
      if (usersData.length > 0 && !selectedUserId) {
        setSelectedUserId(user?.id || usersData[0].id);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProductivityData = async () => {
    try {
      setLoading(true);
      
      const [metrics, comparison] = await Promise.all([
        apiService.getUserProductivityMetrics(selectedUserId, dateRange, weeklyHours),
        apiService.compareUserProductivity(dateRange)
      ]);

      setProductivityData(metrics);
      setTeamComparison(comparison);
    } catch (error) {
      console.error('Error loading productivity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 85) return 'success';
    if (score >= 70) return 'info';
    if (score >= 55) return 'warning';
    return 'error';
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'needs-improvement': return 'warning';
      case 'poor': return 'error';
      default: return 'default';
    }
  };

  // Prepare radar chart data for overall performance
  const prepareRadarData = () => {
    if (!productivityData) return [];
    
    const { performanceIndicators } = productivityData;
    return [
      {
        subject: 'Productivity',
        score: performanceIndicators.productivityScore,
        fullMark: 100
      },
      {
        subject: 'Quality',
        score: productivityData.qualityMetrics.qualityScore,
        fullMark: 100
      },
      {
        subject: 'Reliability',
        score: performanceIndicators.reliabilityScore,
        fullMark: 100
      },
      {
        subject: 'Efficiency',
        score: performanceIndicators.efficiencyScore,
        fullMark: 100
      },
      {
        subject: 'Utilization',
        score: productivityData.allocationAnalysis.utilizationPercentage,
        fullMark: 100
      }
    ];
  };

  if (loading && !productivityData) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const selectedUser = users.find(u => u.id === selectedUserId);
  const userComparison = teamComparison.find(c => c.userId === selectedUserId);

  return (
    <Layout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            User Productivity Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadProductivityData}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select User</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  label="Select User"
                >
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={dateRange.start.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={dateRange.end.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Weekly Hours"
                value={weeklyHours}
                onChange={(e) => setWeeklyHours(parseInt(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        {productivityData && (
          <>
            {/* User Info Header */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5">
                      {productivityData.userName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {productivityData.userEmail} • {productivityData.userRole}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h3">
                      {productivityData.performanceIndicators.overallPerformanceScore}
                    </Typography>
                    <Chip 
                      label={productivityData.performanceIndicators.performanceRating.toUpperCase()}
                      color={getRatingColor(productivityData.performanceIndicators.performanceRating)}
                      sx={{ mt: 1 }}
                    />
                    {userComparison && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Rank: #{userComparison.rank} (Top {userComparison.percentile}%)
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Alerts */}
            {productivityData.alerts.recommendations.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {productivityData.alerts.recommendations.map((rec: string, idx: number) => (
                  <Alert 
                    key={idx} 
                    severity={rec.includes('⚠️') || rec.includes('🆘') ? 'warning' : 'info'}
                    sx={{ mb: 1 }}
                  >
                    {rec}
                  </Alert>
                ))}
              </Box>
            )}

            {/* Key Performance Indicators */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Productivity Score */}
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip title="Overall productivity based on task completion and time utilization" arrow>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" variant="body2">
                            Productivity Score
                          </Typography>
                          <Typography variant="h4">
                            {productivityData.performanceIndicators.productivityScore}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={productivityData.performanceIndicators.productivityScore}
                            color={getPerformanceColor(productivityData.performanceIndicators.productivityScore)}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <Speed color="primary" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Quality Score */}
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip title="Quality of work based on defects and rework rate" arrow>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" variant="body2">
                            Quality Score
                          </Typography>
                          <Typography variant="h4">
                            {productivityData.qualityMetrics.qualityScore}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={productivityData.qualityMetrics.qualityScore}
                            color={getPerformanceColor(productivityData.qualityMetrics.qualityScore)}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <CheckCircle color="success" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Reliability Score */}
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip title="On-time delivery rate and commitment achievement" arrow>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" variant="body2">
                            Reliability Score
                          </Typography>
                          <Typography variant="h4">
                            {productivityData.performanceIndicators.reliabilityScore}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={productivityData.performanceIndicators.reliabilityScore}
                            color={getPerformanceColor(productivityData.performanceIndicators.reliabilityScore)}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <Timeline color="info" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Efficiency Score */}
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip title="Estimation accuracy and time management" arrow>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="textSecondary" variant="body2">
                            Efficiency Score
                          </Typography>
                          <Typography variant="h4">
                            {productivityData.performanceIndicators.efficiencyScore}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={productivityData.performanceIndicators.efficiencyScore}
                            color={getPerformanceColor(productivityData.performanceIndicators.efficiencyScore)}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <AccessTime color="warning" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Performance Radar Chart */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Radar
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={prepareRadarData()}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis domain={[0, 100]} />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="#8884d8"
                          fill="#8884d8"
                          fillOpacity={0.6}
                        />
                        <RechartsTooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Time Analysis */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Time Analysis: Estimated vs Logged
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={[
                        {
                          name: 'Hours',
                          Estimated: productivityData.timeAnalysis.totalEstimatedHours,
                          Logged: productivityData.timeAnalysis.totalLoggedHours
                        }
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="Estimated" fill="#8884d8" />
                        <Bar dataKey="Logged" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Chip 
                        label={`Estimate Accuracy: ${productivityData.timeAnalysis.estimateAccuracy}%`}
                        color={
                          Math.abs(100 - productivityData.timeAnalysis.estimateAccuracy) < 20 ? 'success' : 'warning'
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Allocation Analysis */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Allocation & Utilization Analysis
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={[
                        {
                          name: 'Hours',
                          'Available (excl. leave)': productivityData.allocationAnalysis.totalAvailableHours,
                          'Allocated (tasks)': productivityData.allocationAnalysis.totalAllocatedHours,
                          'Logged (timesheets)': productivityData.allocationAnalysis.totalLoggedHours,
                          'Leave': productivityData.allocationAnalysis.leaveHours
                        }
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="Available (excl. leave)" fill="#4caf50" />
                        <Bar dataKey="Allocated (tasks)" fill="#2196f3" />
                        <Bar dataKey="Logged (timesheets)" fill="#ff9800" />
                        <Bar dataKey="Leave" fill="#9e9e9e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Standard Weekly Hours</Typography>
                        <Typography variant="h6">{productivityData.allocationAnalysis.standardWeeklyHours} hrs</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Weeks in Period</Typography>
                        <Typography variant="h6">{productivityData.allocationAnalysis.weeksInPeriod}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Leave Hours</Typography>
                        <Typography variant="h6">{productivityData.allocationAnalysis.leaveHours} hrs</Typography>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Allocation %</Typography>
                        <Typography variant="h6">
                          {productivityData.allocationAnalysis.allocationPercentage}%
                          {productivityData.alerts.isOverallocated && (
                            <Chip label="OVER" color="error" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">Utilization %</Typography>
                        <Typography variant="h6">
                          {productivityData.allocationAnalysis.utilizationPercentage}%
                          {productivityData.alerts.hasLowUtilization && (
                            <Chip label="LOW" color="warning" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Delivery Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Stories: Committed vs Delivered
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Delivered', value: productivityData.deliveryMetrics.storiesDelivered, fill: '#4caf50' },
                            { name: 'In Progress', value: productivityData.deliveryMetrics.storiesInProgress, fill: '#2196f3' },
                            { name: 'Not Started', value: productivityData.deliveryMetrics.storiesNotStarted, fill: '#9e9e9e' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          dataKey="value"
                        />
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Commitment Delivery Rate: <strong>{productivityData.deliveryMetrics.commitmentDeliveryRate}%</strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        ({productivityData.deliveryMetrics.storiesDelivered} of {productivityData.deliveryMetrics.storiesCommitted} committed)
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timeline Performance */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Timeline Performance
                    </Typography>
                    <Box sx={{ p: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
                            <Typography variant="h4" sx={{ mt: 1 }}>
                              {productivityData.deliveryMetrics.tasksCompletedOnTime}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              On Time
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Warning sx={{ fontSize: 48, color: 'error.main' }} />
                            <Typography variant="h4" sx={{ mt: 1 }}>
                              {productivityData.deliveryMetrics.tasksMissedDeadline}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Missed
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Timeline Missed Rate
                        </Typography>
                        <Typography variant="h5" color={productivityData.deliveryMetrics.timelineMissedRate > 20 ? 'error' : 'success.main'}>
                          {productivityData.deliveryMetrics.timelineMissedRate}%
                        </Typography>
                        {productivityData.deliveryMetrics.averageDeliveryDelay > 0 && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            Avg Delay: {productivityData.deliveryMetrics.averageDeliveryDelay} days
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Quality Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quality Metrics
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Issues Delivered</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.qualityMetrics.totalIssuesDelivered}</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Defects Raised in Work</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={productivityData.qualityMetrics.defectsRaisedInWork}
                                color={productivityData.alerts.hasHighDefectRate ? 'error' : 'success'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Defect Rate</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.qualityMetrics.defectRate}%</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Rework Count</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.qualityMetrics.reworkCount}</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Rework Rate</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.qualityMetrics.reworkRate}%</strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Time Estimate Accuracy */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Time Estimation Accuracy
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Total Estimated Hours</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.timeAnalysis.totalEstimatedHours} hrs</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Total Logged Hours</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.timeAnalysis.totalLoggedHours} hrs</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Estimate Accuracy</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${productivityData.timeAnalysis.estimateAccuracy}%`}
                                color={
                                  Math.abs(100 - productivityData.timeAnalysis.estimateAccuracy) < 20 ? 'success' : 'warning'
                                }
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Over-estimated Tasks</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.timeAnalysis.overEstimatedTasks}</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Under-estimated Tasks</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.timeAnalysis.underEstimatedTasks}</strong>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Avg Time Per Story</TableCell>
                            <TableCell align="right">
                              <strong>{productivityData.timeAnalysis.averageTimePerStory} hrs</strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Recent Activity */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity (Last 30 Days)
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Assignment color="primary" sx={{ fontSize: 36 }} />
                      <Typography variant="h4" sx={{ mt: 1 }}>
                        {productivityData.recentActivity.tasksCompleted}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Tasks Completed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <AccessTime color="info" sx={{ fontSize: 36 }} />
                      <Typography variant="h4" sx={{ mt: 1 }}>
                        {productivityData.recentActivity.hoursLogged}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Hours Logged
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <EventAvailable color="success" sx={{ fontSize: 36 }} />
                      <Typography variant="h4" sx={{ mt: 1 }}>
                        {productivityData.recentActivity.leaveDaysTaken}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Leave Days
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Timeline color="secondary" sx={{ fontSize: 36 }} />
                      <Typography variant="h4" sx={{ mt: 1 }}>
                        {productivityData.recentActivity.sprintsParticipated}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Sprints
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Speed color="warning" sx={{ fontSize: 36 }} />
                      <Typography variant="h4" sx={{ mt: 1 }}>
                        {productivityData.recentActivity.averageTaskCompletionTime}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg Days/Task
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Team Comparison */}
            {teamComparison.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Team Comparison
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell align="right">Productivity Score</TableCell>
                          <TableCell align="right">Percentile</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teamComparison.map((comp) => (
                          <TableRow 
                            key={comp.userId}
                            sx={{ 
                              bgcolor: comp.userId === selectedUserId ? 'action.selected' : 'inherit',
                              fontWeight: comp.userId === selectedUserId ? 'bold' : 'normal'
                            }}
                          >
                            <TableCell>#{comp.rank}</TableCell>
                            <TableCell>
                              {comp.userName}
                              {comp.userId === selectedUserId && (
                                <Chip label="YOU" size="small" color="primary" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <strong>{comp.productivityScore.toFixed(1)}</strong>
                            </TableCell>
                            <TableCell align="right">
                              Top {comp.percentile}%
                            </TableCell>
                            <TableCell align="center">
                              {comp.productivityScore >= 85 && '🌟'}
                              {comp.productivityScore >= 70 && comp.productivityScore < 85 && '✅'}
                              {comp.productivityScore >= 55 && comp.productivityScore < 70 && '📊'}
                              {comp.productivityScore < 55 && '⚠️'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default UserProductivity;

